import {useEffect, useState} from 'react'
import {
  Breadcrumb,
  Button,
  ButtonGroup,
  Col,
  Dropdown,
  Form,
  InputGroup,
  Row,
  Spinner,
} from 'react-bootstrap'
import {useNavigate} from 'react-router-dom'
import {FormattedMessage, useIntl} from 'react-intl'

import {useDisclourse} from '../../hooks/useDisclourse'
import {useLazyQuery, useMutation} from '@apollo/client'
import {GET_ALL_SIGNATURE_FORMS_API, GET_ONE_SIGNATURE_FORM_API} from '../../gql/queries/signatureFormsApiQueries'
import {toast} from 'react-toastify'
import {
  IResSignatureFormsAPI,
  NodeSignatureForm,
} from '../../types/signatureFormsApi'
import {parseId, hasPermission} from '../../helpers'
import moment from 'moment'
import {ModalPreview} from './components/ModalPreview'
import {SIZE_PAGE_DEFAULT, ALL_PERMISSIONS} from '../../helpers/consts'
import {PaginationUtils} from '../shared/PaginationUtils'
import {UPDATE_SIGNATURE_FORM_STATUS} from '../../gql/mutations/signatureFormsMutations'
import {PaginationSection} from '../shared/PaginationSection'
import {ModalAddSignatureForm} from './components/ModalAddSignatureForm'
import {BadgeStatus} from './components/BadgeStatus'
import {useGetUser} from '../../store/userStore'

const bullet = '●'
const slash = '/'
const percentSign = '%'

export const SignatureFormsPage = () => {
  const intl = useIntl()
  const permissions = useGetUser((s) => s.user?.permissions)
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const canEditSignatureForm = permissions && hasPermission(permissions, ALL_PERMISSIONS.EDIT_SIGNATURE_FORMS)
  const canAddSignatureForm = permissions && hasPermission(permissions, ALL_PERMISSIONS.ADD_SIGNATURE_FORMS)

  // Check if user is Agente, Asesor, or Supervisor
  const isRestrictedRole = userGroups.includes('Agente') || userGroups.includes('Asesor') || userGroups.includes('Supervisor')
  

  const [gqlGetSignatureForms, {data: dataAllSignatureForms, loading, error, refetch}] =
    useLazyQuery<IResSignatureFormsAPI>(GET_ALL_SIGNATURE_FORMS_API, {
      fetchPolicy: 'no-cache',
    })

  // Lazy query for fetching one signature form by ID
  const [getSignatureFormById, {data: dataSignatureForm}] = useLazyQuery(GET_ONE_SIGNATURE_FORM_API, {fetchPolicy: 'no-cache'})

  const [updateSignatureFormStatus] = useMutation(UPDATE_SIGNATURE_FORM_STATUS)

  const {isOpen, onClose, onOpen} = useDisclourse()
  const {isOpen: isOpenPreview, onClose: onClosePreview, onOpen: onOpenPreview} = useDisclourse()

  const [infoPreview, setInfoPreview] = useState<null | NodeSignatureForm>(null)
  // 'new' | 'edit' | 'duplicate' | null
  const [mode, setMode] = useState<null | 'new' | 'edit' | 'duplicate'>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [first, setFirst] = useState(SIZE_PAGE_DEFAULT)

  const [isPag] = useState(false)
  const [page, setPage] = useState(1)

  const [dataArr, setDataArr] = useState<IResSignatureFormsAPI['allSignatureForms']['edges']>([])

  let totalSignatureForms = dataAllSignatureForms?.allSignatureForms?.totalCount ?? 0

  const totalPages = Math.ceil(totalSignatureForms / first)
  const {start, end} = PaginationUtils.getPaginationRange(currentPage, first, totalSignatureForms)

  const navigate = useNavigate()


  const initialFormData = {
    name: '',
    description: '',
    formContent: '',
    emailContent: '',
    smsContent: '',
    status: '', // 'ACTIVE' | 'INACTIVE'
    user: '', // ID del usuario creador
  }

  const [formData, setFormData] = useState(initialFormData)

  const handleFilters = () => {
    const variables = {
      first: first,
      offset: (currentPage - 1) * first,
      name: formData.name || null,
      status: filterStatus === 'all' ? null : filterStatus,
    }
    gqlGetSignatureForms({
      variables,
    })
  }

  type IFilterStatus = 'all' | 'ACTIVE' | 'INACTIVE'
  const [filterStatus, setFilterStatus] = useState<IFilterStatus>('ACTIVE')

  useEffect(() => {
    handleFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first, gqlGetSignatureForms, currentPage])

  const handlePolicyName = () => {
    handleFilters()
  }

  useEffect(() => {
    if (!dataAllSignatureForms) return
    const {edges} = dataAllSignatureForms.allSignatureForms
    // if (edges.length === 0) return
    if (isPag) {
      setDataArr((p) => [...p, ...edges])
    } else {
      setDataArr(edges)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAllSignatureForms])

  useEffect(() => {
    if (error) {
      toast.error(
        intl.formatMessage(
          {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
          {message: error.message}
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const updatePage = (n: number) => {
    setCurrentPage(n)
  }

  // if ((loading || !dataAllSignatureForms) && !isPag) {
  //   return <LoadingSignatureForms />
  // }

  return (
    <div className='signatureForms-page'>
      <header className='header'>
        <Breadcrumb>
          <Breadcrumb.Item className='fs-3'>
            <FormattedMessage
              id='documents.signatureForms.list.breadcrumb'
              defaultMessage='Signature forms list'
            />
          </Breadcrumb.Item>
        </Breadcrumb>
        <div className='d-flex align-items-center gap-2'>
          {canAddSignatureForm && (
            <Button
              size='sm'
              onClick={() => {
                setMode('new')
                setInfoPreview(null)
                onOpen()
              }}
            >
              <i className='bi bi-plus fs-3' /> <FormattedMessage
                id='documents.signatureForms.list.newForm'
                defaultMessage='New signature form'
              />
            </Button>
          )}
        </div>
      </header>
      <Row className='signatureForms'>
        <Col className='container'>
          <div className='signatureForms-container'>
            <div className='title-header'>
              <h3>
                <FormattedMessage
                  id='documents.signatureForms.list.title'
                  defaultMessage='All signature forms'
                />
              </h3>
            </div>
            <div className='filters-header'>
              <InputGroup>
                <Form.Control
                  disabled={false}
                  placeholder={intl.formatMessage({
                    id: 'documents.signatureForms.list.searchPlaceholder',
                    defaultMessage: 'Type your query here...',
                  })}
                  aria-label={intl.formatMessage({
                    id: 'documents.signatureForms.list.searchAriaLabel',
                    defaultMessage: 'Search signature forms',
                  })}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault() // evita el submit automático
                      handlePolicyName() // si tienes una función como esa
                    }
                  }}
                  value={formData.name}
                />
                <Form.Select
                  value={filterStatus}
                  aria-label={intl.formatMessage({
                    id: 'documents.signatureForms.list.filterByStatus',
                    defaultMessage: 'Filter by status',
                  })}
                  onChange={(e) => setFilterStatus(e.target.value as IFilterStatus)}
                >
                  <option value='all'>
                    {intl.formatMessage({
                      id: 'documents.signatureForms.list.filterByStatus',
                      defaultMessage: 'Filter by status',
                    })}
                  </option>
                  <option value='ACTIVE'>
                    {intl.formatMessage({
                      id: 'common.status.active',
                      defaultMessage: 'Active',
                    })}
                  </option>
                  <option value='INACTIVE'>
                    {intl.formatMessage({
                      id: 'common.status.inactive',
                      defaultMessage: 'Inactive',
                    })}
                  </option>
                </Form.Select>
                <Button
                  disabled={false}
                  onClick={() => handleFilters()}
                  variant='outline-success'
                  style={{border: '1px solid #dbdfe9'}}
                >
                  <FormattedMessage
                    id='common.action.search'
                    defaultMessage='Search'
                  />
                </Button>
                <Button
                  disabled={false}
                  variant='outline-primary'
                  style={{border: '1px solid #dbdfe9'}}
                >
                  <FormattedMessage
                    id='common.action.filter'
                    defaultMessage='Filter'
                  />
                </Button>
              </InputGroup>
            </div>
            <div className='results-header'>
              <PaginationSection
                first={first}
                setFirst={(n) => {
                  setFirst(n)
                  setPage(1)
                }}
                currentPage={currentPage}
                totalPages={totalPages}
                start={start}
                end={end}
                totalPolicies={totalSignatureForms}
                updatePage={updatePage}
                PaginationUtils={PaginationUtils}
              />
              <div className='col-md-3'>
                <div className='results-filter'>
                  <Col lg={12} md={12} sm={12} xs={12}></Col>
                </div>
              </div>
            </div>

            <div className='table-responsive table table-striped table-hover '>
              <table
                className='table-scrollable table-scroll  table table-striped table-hover '
                style={{whiteSpace: 'nowrap'}}
              >
                <thead>
                  <tr role='row'>
                    <th></th>
                    <th className='fw-bold'>
                      <FormattedMessage id='common.field.name' defaultMessage='Name' />
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage id='common.field.status' defaultMessage='Status' />
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage
                        id='common.field.created'
                        defaultMessage='Created'
                      />
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage
                        id='documents.signatureForms.list.col.createdBy'
                        defaultMessage='Created by'
                      />
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage
                        id='documents.signatureForms.list.col.signedRequests'
                        defaultMessage='Signed / requests'
                      />
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage id='common.field.actions' defaultMessage='Actions' />
                    </th>
                  </tr>
                </thead>
                {(loading || !dataAllSignatureForms) && !isPag ? (
                  <tbody>
                    <tr>
                      <td colSpan={8}>
                        <div className='d-flex flex-center' style={{height: '30vh'}}>
                          <Spinner animation='border' />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody>
                    {dataArr.length === 0 && (
                      <tr>
                        <td colSpan={8}>
                          <div className='d-flex flex-center' style={{height: '30vh'}}>
                            <FormattedMessage
                              id='documents.signatureForms.list.empty'
                              defaultMessage='There are no signature forms currently added'
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                    {loading && (
                      <tr>
                        <td colSpan={8}>
                          <div className='d-flex flex-center' style={{height: '30vh'}}>
                            <Spinner animation='border' />
                          </div>
                        </td>
                      </tr>
                    )}
                    {dataArr.slice(first * page - first, first * page).map(({node: nodeSF}) => (
                      <tr key={nodeSF.id}>
                        <td>
                          <div className='set-quick-status'>
                            <div></div>
                          </div>
                        </td>
                        <td>
                          <div>
                            <span
                              className='text-primary'
                              style={{cursor: 'pointer'}}
                              data-tooltip-id={`myt2${parseId(nodeSF.id)}`}
                            >
                              {nodeSF.name}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div>
                            <BadgeStatus state={nodeSF.status} />
                          </div>
                        </td>
                        {/* <td>
                        
                      </td> */}
                        <td>{moment(nodeSF.created).format('DD MMM, YY')}</td>
                        <td>
                          {nodeSF.user?.firstName} {nodeSF.user?.lastName}
                        </td>
                        <td>
                          <div style={{display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '200px'}}>
                            <div style={{fontSize: '13px', color: '#5e6278'}}>
                              <span style={{color: '#50cd89', marginRight: '4px'}}>{bullet}</span>
                              <FormattedMessage
                                id='documents.signatureForms.list.signedCount'
                                defaultMessage='Signed ({count})'
                                values={{count: nodeSF.signedEntriesCount ?? 0}}
                              /> {slash}
                              <span style={{color: '#ffc700', marginLeft: '4px'}}>{bullet}</span>
                              <span style={{marginLeft: '4px'}}>
                                <FormattedMessage
                                  id='documents.signatureForms.list.requestsCount'
                                  defaultMessage='Requests ({count})'
                                  values={{count: nodeSF.entriesCount ?? 0}}
                                />
                              </span>
                            </div>
                            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                              <div style={{
                                flex: 1,
                                height: '24px',
                                backgroundColor: '#ffc700',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                position: 'relative'
                              }}>
                                <div style={{
                                  height: '100%',
                                  backgroundColor: '#50cd89',
                                  width: `${nodeSF.entriesCount ? Math.round((nodeSF.signedEntriesCount ?? 0) / nodeSF.entriesCount * 100) : 0}%`,
                                  transition: 'width 0.3s ease'
                                }}></div>
                                <span style={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  color: '#fff',
                                  textShadow: '0 0 2px rgba(0,0,0,0.5)'
                                }}>
                                  {nodeSF.entriesCount ? Math.round((nodeSF.signedEntriesCount ?? 0) / nodeSF.entriesCount * 100) : 0}
                                  {percentSign}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <Dropdown as={ButtonGroup}>
                            <Button
                              variant='outline-primary'
                              size='sm'
                              style={{border: '1px solid #3e97ff'}}
                              onClick={() => {
                                getSignatureFormById({variables: {id: parseId(nodeSF.id)}})
                                onOpenPreview()
                              }}
                            >
                              <FormattedMessage
                                id='common.action.preview'
                                defaultMessage='Preview'
                              />
                            </Button>
                            <Dropdown.Toggle
                              split
                              variant='outline-primary'
                              size='sm'
                              id='dropdown-custom-2'
                              style={{border: '1px solid #3e97ff'}}
                            />
                            <Dropdown.Menu className='dropdown-menu-signatureForm'>
                              {(nodeSF.entriesCount ?? 0) > 0 && (
                                <Dropdown.Item
                                  eventKey='entries'
                                  onClick={() => navigate(`/signature-forms/${nodeSF.id}/entries`)}
                                >
                                  <i className='bi bi-arrow-repeat' /> <FormattedMessage
                                    id='documents.signatureForms.list.action.viewEntries'
                                    defaultMessage='View entries'
                                  />
                                </Dropdown.Item>
                              )}
                              {canEditSignatureForm && (
                              <>
                                {nodeSF.status === 'INACTIVE' && !isRestrictedRole && (
                                  <Dropdown.Item
                                    eventKey='activate'
                                    onClick={async () => {
                                      try {
                                        const {data} = await updateSignatureFormStatus({
                                          variables: {id: parseId(nodeSF.id), status: 'ACTIVE'},
                                        })
                                        if (data?.signatureFormUpdateStatus?.success) {
                                          toast.success(
                                            intl.formatMessage({
                                              id: 'documents.signatureForms.list.toast.markedActive',
                                              defaultMessage: 'Signature form marked as active',
                                            })
                                          )
                                          refetch()
                                        } else {
                                          toast.error(
                                            data?.signatureFormUpdateStatus?.errors?.[0]?.message ||
                                              intl.formatMessage({
                                                id: 'documents.signatureForms.list.toast.updateStatusError',
                                                defaultMessage: 'Error updating status',
                                              })
                                          )
                                        }
                                      } catch (e: any) {
                                        toast.error(e.message)
                                      }
                                    }}
                                  >
                                    <i className='bi bi-check-square-fill' /> <FormattedMessage
                                      id='documents.signatureForms.list.action.markActive'
                                      defaultMessage='Mark as active'
                                    />
                                  </Dropdown.Item>
                                )}

                                {nodeSF.status === 'ACTIVE' && !isRestrictedRole && (
                                  <Dropdown.Item
                                    eventKey='deactivate'
                                    onClick={async () => {
                                      try {
                                        const {data} = await updateSignatureFormStatus({
                                          variables: {id: parseId(nodeSF.id), status: 'INACTIVE'},
                                        })
                                        if (data?.signatureFormUpdateStatus?.success) {
                                          toast.success(
                                            intl.formatMessage({
                                              id: 'documents.signatureForms.list.toast.markedInactive',
                                              defaultMessage: 'Signature form marked as inactive',
                                            })
                                          )
                                          refetch()
                                        } else {
                                          toast.error(
                                            data?.signatureFormUpdateStatus?.errors?.[0]?.message ||
                                              intl.formatMessage({
                                                id: 'documents.signatureForms.list.toast.updateStatusError',
                                                defaultMessage: 'Error updating status',
                                              })
                                          )
                                        }
                                      } catch (e: any) {
                                        toast.error(e.message)
                                      }
                                    }}
                                  >
                                    <i className='bi bi-folder-fill' /> <FormattedMessage
                                      id='documents.signatureForms.list.action.markInactive'
                                      defaultMessage='Mark as inactive'
                                    />
                                  </Dropdown.Item>
                                )}
                                <Dropdown.Divider />
                                <Dropdown.Item
                                  eventKey='edit'
                                  onClick={async (e) => {
                                    e.preventDefault()
                                    setMode('edit')
                                    await getSignatureFormById({variables: {id: parseId(nodeSF.id)}})
                                    setInfoPreview(null)
                                    onOpen()
                                  }}
                                >
                                  <i className='bi bi-pencil-fill' /> <FormattedMessage
                                    id='common.action.edit'
                                    defaultMessage='Edit'
                                  />
                                </Dropdown.Item>
                                {!isRestrictedRole && (
                                  <Dropdown.Item
                                    eventKey='duplicate'
                                    onClick={async (e) => {
                                      e.preventDefault()
                                      setMode('duplicate')
                                      const result = await getSignatureFormById({variables: {id: parseId(nodeSF.id)}})
                                      const form = result?.data?.signatureFormById
                                      if (form) {
                                        setInfoPreview({
                                          ...form,
                                          id: '', // aseguramos que sea tratado como nuevo
                                          name: intl.formatMessage(
                                            {
                                              id: 'documents.signatureForms.list.copySuffix',
                                              defaultMessage: '{name} Copy',
                                            },
                                            {name: form.name}
                                          ),
                                        })
                                        // Ensure dataSignatureForm is not used in duplicate mode
                                      }
                                      onOpen()
                                    }}
                                  >
                                    <i className='bi bi-files' /> <FormattedMessage
                                      id='documents.signatureForms.list.action.duplicate'
                                      defaultMessage='Duplicate form'
                                    />
                                  </Dropdown.Item>
                                )}
                              </>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          </div>
        </Col>
      </Row>

      {isOpen && (
        <ModalAddSignatureForm
          refetch={refetch}
          isOpen={isOpen}
          onClose={onClose}
          keyboard={false}
          backdrop='static'
          idFormEdit={
            mode === 'duplicate'
              ? infoPreview
              : mode === 'edit'
              ? dataSignatureForm?.signatureFormById
              : null
          }
        />
      )}
      {isOpenPreview && dataSignatureForm?.signatureFormById && (
        <ModalPreview
          info={dataSignatureForm.signatureFormById}
          isOpen={isOpenPreview}
          onClose={onClosePreview}
        />
      )}
    </div>
  )
}
